import { useFormik } from "formik"
import { enqueueSnackbar } from "notistack"
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { boolean, object, string } from "yup"

import { ProjectFormPageProps } from "components/ProjectForm"
import { EProjectFormPages } from "components/ProjectForm/EProjectFormPages"
import { useTemplateUIRules } from "components/ProjectForm/context/TemplateUIRulesContext"
import { setAddOnsForm } from "components/ProjectForm/ProjectFormSlice"
import { EFileTypes } from "enums/fileTypeEnums"
import { DropdownObj } from "pages/Common/commonTypes"
import { SubmitFileInfo, useSubmitFinalUploadsMutation } from "pages/Common/fileUploadService"
import {
    IProjectFormDocument,
    useLazyGetProjectFormDetailsByStepQuery,
    useUpdateTicketPageDataMutation,
} from "pages/Projects/ProjectServices"
import { TStore } from "store/Store"
import { formatGMTDateToYYYYMMDD } from "utils/utils"

export enum EAddOnsFields {
    IS_WARRANTY_APPLICABLE = "is_warranty_applicable",
    WARRANTY_DURATION_IN_MONTHS = "warranty_duration_in_months",
    WARRANTY_START_CRITERIA = "warranty_start_criteria",
    WARRANTY_START_DATE = "warranty_start_date",

    IS_AMC_APPLICABLE = "is_amc_applicable",
    AMC_TYPE = "amc_type",
    AMC_START_DATE = "amc_start_date",
    AMC_DURATION = "amc_duration",

    IS_MANPOWER_APPLICABLE = "is_manpower_applicable",
    MANPOWER_MARSHAL_ENABLED = "manpower_marshal_enabled",
    MANPOWER_MARSHAL_RESOURCE = "manpower_marshal_resource",
    MANPOWER_MARSHAL_DURATION = "manpower_marshal_duration",
    MANPOWER_GUARD_ENABLED = "manpower_guard_enabled",
    MANPOWER_GUARD_RESOURCE = "manpower_guard_resource",
    MANPOWER_GUARD_DURATION = "manpower_guard_duration",
    MANPOWER_CUSTOMER_SUPPORT_ENABLED = "manpower_customer_support_enabled",
    MANPOWER_CUSTOMER_SUPPORT_RESOURCE = "manpower_customer_support_resource",
    MANPOWER_CUSTOMER_SUPPORT_DURATION = "manpower_customer_support_duration",

    CHARGER_CONNECTIVITY_REQUIRED = "charger_connectivity_required",
    CHARGER_CONNECTION_TYPE = "charger_connection_type",
    SIM_DURATION = "sim_duration",

    IS_O_AND_M_REQUIRED = "is_o_and_m_required",
    INSURANCE_REQUIRED = "insurance_required",
}

export type ManpowerObj = {
    manpower_type: string
    resource: number
    duration: number
}

export type AddOnsFormValues = {
    [EAddOnsFields.IS_WARRANTY_APPLICABLE]: boolean
    [EAddOnsFields.WARRANTY_DURATION_IN_MONTHS]: string
    [EAddOnsFields.WARRANTY_START_CRITERIA]: DropdownObj | null
    [EAddOnsFields.WARRANTY_START_DATE]: string

    [EAddOnsFields.IS_AMC_APPLICABLE]: boolean
    [EAddOnsFields.AMC_TYPE]: DropdownObj | null
    [EAddOnsFields.AMC_START_DATE]: string
    [EAddOnsFields.AMC_DURATION]: string

    [EAddOnsFields.IS_MANPOWER_APPLICABLE]: boolean
    [EAddOnsFields.MANPOWER_MARSHAL_ENABLED]: boolean
    [EAddOnsFields.MANPOWER_MARSHAL_RESOURCE]: string
    [EAddOnsFields.MANPOWER_MARSHAL_DURATION]: string
    [EAddOnsFields.MANPOWER_GUARD_ENABLED]: boolean
    [EAddOnsFields.MANPOWER_GUARD_RESOURCE]: string
    [EAddOnsFields.MANPOWER_GUARD_DURATION]: string
    [EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_ENABLED]: boolean
    [EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_RESOURCE]: string
    [EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_DURATION]: string

    [EAddOnsFields.CHARGER_CONNECTIVITY_REQUIRED]: boolean
    [EAddOnsFields.CHARGER_CONNECTION_TYPE]: DropdownObj | null
    [EAddOnsFields.SIM_DURATION]: string

    [EAddOnsFields.IS_O_AND_M_REQUIRED]: boolean
    [EAddOnsFields.INSURANCE_REQUIRED]: boolean
}

const initialValues: AddOnsFormValues = {
    [EAddOnsFields.IS_WARRANTY_APPLICABLE]: false,
    [EAddOnsFields.WARRANTY_DURATION_IN_MONTHS]: "",
    [EAddOnsFields.WARRANTY_START_CRITERIA]: null,
    [EAddOnsFields.WARRANTY_START_DATE]: "",

    [EAddOnsFields.IS_AMC_APPLICABLE]: false,
    [EAddOnsFields.AMC_TYPE]: null,
    [EAddOnsFields.AMC_START_DATE]: "",
    [EAddOnsFields.AMC_DURATION]: "",

    [EAddOnsFields.IS_MANPOWER_APPLICABLE]: false,
    [EAddOnsFields.MANPOWER_MARSHAL_ENABLED]: false,
    [EAddOnsFields.MANPOWER_MARSHAL_RESOURCE]: "",
    [EAddOnsFields.MANPOWER_MARSHAL_DURATION]: "",
    [EAddOnsFields.MANPOWER_GUARD_ENABLED]: false,
    [EAddOnsFields.MANPOWER_GUARD_RESOURCE]: "",
    [EAddOnsFields.MANPOWER_GUARD_DURATION]: "",
    [EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_ENABLED]: false,
    [EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_RESOURCE]: "",
    [EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_DURATION]: "",

    [EAddOnsFields.CHARGER_CONNECTIVITY_REQUIRED]: false,
    [EAddOnsFields.CHARGER_CONNECTION_TYPE]: null,
    [EAddOnsFields.SIM_DURATION]: "",

    [EAddOnsFields.IS_O_AND_M_REQUIRED]: false,
    [EAddOnsFields.INSURANCE_REQUIRED]: false,
}

const validationSchema = object({
    [EAddOnsFields.IS_WARRANTY_APPLICABLE]: boolean().required(),
    [EAddOnsFields.WARRANTY_DURATION_IN_MONTHS]: string().when(EAddOnsFields.IS_WARRANTY_APPLICABLE, {
        is: true,
        then: schema => schema.required("Warranty duration is required"),
        otherwise: schema => schema.default(""),
    }),
    [EAddOnsFields.WARRANTY_START_CRITERIA]: object()
        .nullable()
        .when(EAddOnsFields.IS_WARRANTY_APPLICABLE, {
            is: true,
            then: schema => schema.required("Warranty start criteria is required"),
            otherwise: schema => schema.nullable().default(null),
        }),
    [EAddOnsFields.WARRANTY_START_DATE]: string().when(
        [EAddOnsFields.IS_WARRANTY_APPLICABLE, EAddOnsFields.WARRANTY_START_CRITERIA],
        {
            is: (isWarranty: boolean, criteria: { id: number; title: string } | null) =>
                isWarranty && criteria?.title === "Specific Date",
            then: schema => schema.required("Warranty start date is required"),
            otherwise: schema => schema.default(""),
        }
    ),

    [EAddOnsFields.IS_AMC_APPLICABLE]: boolean().required(),
    [EAddOnsFields.AMC_TYPE]: object()
        .nullable()
        .when(EAddOnsFields.IS_AMC_APPLICABLE, {
            is: true,
            then: schema => schema.required("AMC type is required"),
            otherwise: schema => schema.nullable().default(null),
        }),
    [EAddOnsFields.AMC_START_DATE]: string().when(EAddOnsFields.IS_AMC_APPLICABLE, {
        is: true,
        then: schema => schema.required("AMC start date is required"),
        otherwise: schema => schema.default(""),
    }),
    [EAddOnsFields.AMC_DURATION]: string().when(EAddOnsFields.IS_AMC_APPLICABLE, {
        is: true,
        then: schema => schema.required("AMC duration is required"),
        otherwise: schema => schema.default(""),
    }),

    [EAddOnsFields.IS_MANPOWER_APPLICABLE]: boolean().required(),
    [EAddOnsFields.MANPOWER_MARSHAL_ENABLED]: boolean().default(false),
    [EAddOnsFields.MANPOWER_MARSHAL_RESOURCE]: string().when(
        [EAddOnsFields.IS_MANPOWER_APPLICABLE, EAddOnsFields.MANPOWER_MARSHAL_ENABLED],
        {
            is: (isManpower: boolean, isEnabled: boolean) => isManpower && isEnabled,
            then: schema => schema.required("Number of resources is required"),
            otherwise: schema => schema.default(""),
        }
    ),
    [EAddOnsFields.MANPOWER_MARSHAL_DURATION]: string().when(
        [EAddOnsFields.IS_MANPOWER_APPLICABLE, EAddOnsFields.MANPOWER_MARSHAL_ENABLED],
        {
            is: (isManpower: boolean, isEnabled: boolean) => isManpower && isEnabled,
            then: schema => schema.required("Duration is required"),
            otherwise: schema => schema.default(""),
        }
    ),
    [EAddOnsFields.MANPOWER_GUARD_ENABLED]: boolean().default(false),
    [EAddOnsFields.MANPOWER_GUARD_RESOURCE]: string().when(
        [EAddOnsFields.IS_MANPOWER_APPLICABLE, EAddOnsFields.MANPOWER_GUARD_ENABLED],
        {
            is: (isManpower: boolean, isEnabled: boolean) => isManpower && isEnabled,
            then: schema => schema.required("Number of resources is required"),
            otherwise: schema => schema.default(""),
        }
    ),
    [EAddOnsFields.MANPOWER_GUARD_DURATION]: string().when(
        [EAddOnsFields.IS_MANPOWER_APPLICABLE, EAddOnsFields.MANPOWER_GUARD_ENABLED],
        {
            is: (isManpower: boolean, isEnabled: boolean) => isManpower && isEnabled,
            then: schema => schema.required("Duration is required"),
            otherwise: schema => schema.default(""),
        }
    ),
    [EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_ENABLED]: boolean().default(false),
    [EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_RESOURCE]: string().when(
        [EAddOnsFields.IS_MANPOWER_APPLICABLE, EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_ENABLED],
        {
            is: (isManpower: boolean, isEnabled: boolean) => isManpower && isEnabled,
            then: schema => schema.required("Number of resources is required"),
            otherwise: schema => schema.default(""),
        }
    ),
    [EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_DURATION]: string().when(
        [EAddOnsFields.IS_MANPOWER_APPLICABLE, EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_ENABLED],
        {
            is: (isManpower: boolean, isEnabled: boolean) => isManpower && isEnabled,
            then: schema => schema.required("Duration is required"),
            otherwise: schema => schema.default(""),
        }
    ),

    [EAddOnsFields.CHARGER_CONNECTIVITY_REQUIRED]: boolean().required(),
    [EAddOnsFields.CHARGER_CONNECTION_TYPE]: object()
        .nullable()
        .when(EAddOnsFields.CHARGER_CONNECTIVITY_REQUIRED, {
            is: true,
            then: schema => schema.required("Charger connection type is required"),
            otherwise: schema => schema.nullable().default(null),
        }),
    [EAddOnsFields.SIM_DURATION]: string().when(
        [EAddOnsFields.CHARGER_CONNECTIVITY_REQUIRED, EAddOnsFields.CHARGER_CONNECTION_TYPE],
        {
            is: (isConnectivity: boolean, connectionType: { id: number; title: string } | null) =>
                isConnectivity && connectionType?.title === "SIM",
            then: schema => schema.required("SIM duration is required"),
            otherwise: schema => schema.default(""),
        }
    ),

    [EAddOnsFields.IS_O_AND_M_REQUIRED]: boolean().required(),
    [EAddOnsFields.INSURANCE_REQUIRED]: boolean().required(),
}).test(
    "at-least-one-manpower-type",
    "At least one manpower type must be selected and filled",
    function (values): boolean {
        if (!values || !values[EAddOnsFields.IS_MANPOWER_APPLICABLE]) return true

        const hasValidMarshal =
            values[EAddOnsFields.MANPOWER_MARSHAL_ENABLED] &&
            values[EAddOnsFields.MANPOWER_MARSHAL_RESOURCE] &&
            values[EAddOnsFields.MANPOWER_MARSHAL_DURATION]

        const hasValidGuard =
            values[EAddOnsFields.MANPOWER_GUARD_ENABLED] &&
            values[EAddOnsFields.MANPOWER_GUARD_RESOURCE] &&
            values[EAddOnsFields.MANPOWER_GUARD_DURATION]

        const hasValidCustomerSupport =
            values[EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_ENABLED] &&
            values[EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_RESOURCE] &&
            values[EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_DURATION]

        return !!(hasValidMarshal || hasValidGuard || hasValidCustomerSupport)
    }
)

export interface IAddOnsPageData {
    page_title: EProjectFormPages.ADD_ONS
    project_id: number
    is_manpower_applicable: boolean
    is_o_and_m_required: boolean
    is_warranty_applicable: boolean
    is_amc_applicable: boolean
    insurance_required: boolean
    warranty_duration_in_months?: number
    warranty_start_date?: string
    amc_type?: string
    amc_start_date?: string
    amc_duration?: number
    manpower_obj?: ManpowerObj[]
    charger_connectivity_required: boolean
    charger_connection_type?: string
    sim_duration?: number
    warranty_start_criteria?: string
}

const useAddOns = (props: ProjectFormPageProps) => {
    const dispatch = useDispatch()
    const { dealTypeOverrides } = useTemplateUIRules()
    const hasAppliedDefaults = useRef(false)
    const hasPrefilled = useRef(false)

    const yesNoOptions = [
        { id: 1, title: "Yes" },
        { id: 2, title: "No" },
    ]

    const amcTypeOptions = [{ id: 1, title: "Standard" }]

    const warrantyStartCriteriaOptions = [
        { id: 1, title: "Commissioning Date" },
        { id: 2, title: "Installation Date" },
        { id: 3, title: "Invoice Date" },
        { id: 4, title: "Specific Date" },
        { id: 5, title: "Delivery Date" },
    ]

    const chargerConnectionTypeOptions = [
        { id: 1, title: "SIM" },
        { id: 2, title: "WiFi" },
    ]

    const { project_id, add_ons_form } = useSelector((store: TStore) => store.ProjectForm)
    const [updateTicketPageData, { isLoading: isUpdatingPageData }] = useUpdateTicketPageDataMutation()
    const [submitFinalUploads, { isLoading: isSubmittingFinalUploads }] = useSubmitFinalUploadsMutation()
    const [getProjectFormDetailsByStep] = useLazyGetProjectFormDetailsByStepQuery()
    const [existingAmcDocuments, setExistingAmcDocuments] = useState<IProjectFormDocument[]>([])

    const pendingFileInfosRef = useRef<SubmitFileInfo[]>([])

    const FILE_TYPES = {
        AMC_DOCUMENT: EFileTypes.AMC,
    }

    const handleFileUpload = (fileInfos: SubmitFileInfo[], previousFileInfos?: SubmitFileInfo[]) => {
        const prevDocIds = new Set(previousFileInfos?.map(f => f.doc_id))
        pendingFileInfosRef.current = [
            ...pendingFileInfosRef.current.filter(f => !prevDocIds.has(f.doc_id)),
            ...fileInfos,
        ]
    }

    const submitPendingFiles = async () => {
        if (pendingFileInfosRef.current.length === 0 || !project_id) return
        try {
            await submitFinalUploads({
                ticket_id: project_id,
                file_infos: pendingFileInfosRef.current,
            }).unwrap()
            pendingFileInfosRef.current = []
        } catch (error) {
            console.error("Error submitting final uploads:", error)
        }
    }

    const formik = useFormik<AddOnsFormValues>({
        initialValues: add_ons_form ? { ...add_ons_form } : initialValues,
        validationSchema,
        validateOnChange: false,
        onSubmit: handleSubmit,
        enableReinitialize: true,
    })

    const fetchAndPrefillAddOnsData = useCallback(async () => {
        if (!props.isEditMode || !props.projectId || hasPrefilled.current) return

        try {
            const response = await getProjectFormDetailsByStep({
                project_id: props.projectId,
                page_title: EProjectFormPages.ADD_ONS,
            }).unwrap()
            const documents = (response?.documents || []) as IProjectFormDocument[]
            const pageFormData = response?.pages?.[EProjectFormPages.ADD_ONS]
            if (!pageFormData) return

            const amcDocuments = documents.filter(
                doc => doc.document_type === EFileTypes.AMC || doc.link?.doc_type === EFileTypes.AMC
            )
            setExistingAmcDocuments(amcDocuments)

            hasPrefilled.current = true
            const data = pageFormData.page_data || pageFormData

            if (data.is_warranty_applicable != null)
                formik.setFieldValue(EAddOnsFields.IS_WARRANTY_APPLICABLE, !!data.is_warranty_applicable)
            if (data.warranty_duration_in_months != null)
                formik.setFieldValue(
                    EAddOnsFields.WARRANTY_DURATION_IN_MONTHS,
                    String(data.warranty_duration_in_months)
                )
            if (data.warranty_start_criteria) {
                const matched = warrantyStartCriteriaOptions.find(
                    o => o.title.toLowerCase() === data.warranty_start_criteria.toLowerCase()
                )
                if (matched) formik.setFieldValue(EAddOnsFields.WARRANTY_START_CRITERIA, matched)
            }
            if (data.warranty_start_date)
                formik.setFieldValue(EAddOnsFields.WARRANTY_START_DATE, data.warranty_start_date)

            if (data.is_amc_applicable != null)
                formik.setFieldValue(EAddOnsFields.IS_AMC_APPLICABLE, !!data.is_amc_applicable)
            if (data.amc_type) {
                const matched = amcTypeOptions.find(o => o.title.toLowerCase() === data.amc_type.toLowerCase())
                if (matched) formik.setFieldValue(EAddOnsFields.AMC_TYPE, matched)
            }
            if (data.amc_start_date)
                formik.setFieldValue(EAddOnsFields.AMC_START_DATE, formatGMTDateToYYYYMMDD(data.amc_start_date))
            if (data.amc_duration != null) formik.setFieldValue(EAddOnsFields.AMC_DURATION, String(data.amc_duration))

            if (data.is_manpower_applicable != null)
                formik.setFieldValue(EAddOnsFields.IS_MANPOWER_APPLICABLE, !!data.is_manpower_applicable)
            if (data.manpower_obj?.length > 0) {
                data.manpower_obj.forEach((mp: any) => {
                    const type = mp.manpower_type?.toLowerCase()
                    if (type === "marshal") {
                        formik.setFieldValue(EAddOnsFields.MANPOWER_MARSHAL_ENABLED, true)
                        formik.setFieldValue(EAddOnsFields.MANPOWER_MARSHAL_RESOURCE, String(mp.resource || ""))
                        formik.setFieldValue(EAddOnsFields.MANPOWER_MARSHAL_DURATION, String(mp.duration || ""))
                    } else if (type === "guard") {
                        formik.setFieldValue(EAddOnsFields.MANPOWER_GUARD_ENABLED, true)
                        formik.setFieldValue(EAddOnsFields.MANPOWER_GUARD_RESOURCE, String(mp.resource || ""))
                        formik.setFieldValue(EAddOnsFields.MANPOWER_GUARD_DURATION, String(mp.duration || ""))
                    } else if (type === "customer support") {
                        formik.setFieldValue(EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_ENABLED, true)
                        formik.setFieldValue(
                            EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_RESOURCE,
                            String(mp.resource || "")
                        )
                        formik.setFieldValue(
                            EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_DURATION,
                            String(mp.duration || "")
                        )
                    }
                })
            }

            if (data.charger_connectivity_required != null)
                formik.setFieldValue(EAddOnsFields.CHARGER_CONNECTIVITY_REQUIRED, !!data.charger_connectivity_required)
            if (data.charger_connection_type) {
                const matched = chargerConnectionTypeOptions.find(
                    o => o.title.toLowerCase() === data.charger_connection_type.toLowerCase()
                )
                if (matched) formik.setFieldValue(EAddOnsFields.CHARGER_CONNECTION_TYPE, matched)
            }
            if (data.sim_duration != null) formik.setFieldValue(EAddOnsFields.SIM_DURATION, String(data.sim_duration))

            if (data.is_o_and_m_required != null)
                formik.setFieldValue(EAddOnsFields.IS_O_AND_M_REQUIRED, !!data.is_o_and_m_required)
            if (data.insurance_required != null)
                formik.setFieldValue(EAddOnsFields.INSURANCE_REQUIRED, !!data.insurance_required)

            hasAppliedDefaults.current = true
        } catch (error) {
            console.error("Error fetching add-ons prefill data:", error)
        }
    }, [props.isEditMode, props.projectId, getProjectFormDetailsByStep, formik])

    useEffect(() => {
        fetchAndPrefillAddOnsData()
    }, [fetchAndPrefillAddOnsData])

    useEffect(() => {
        if (hasAppliedDefaults.current || add_ons_form) return

        const cpoRules = dealTypeOverrides?.cpo
        if (cpoRules && dealTypeOverrides?.active === "cpo") {
            hasAppliedDefaults.current = true

            if (cpoRules.warranty_default_months) {
                formik.setFieldValue(EAddOnsFields.IS_WARRANTY_APPLICABLE, true)
                formik.setFieldValue(
                    EAddOnsFields.WARRANTY_DURATION_IN_MONTHS,
                    String(cpoRules.warranty_default_months)
                )
            }

            if (cpoRules.warranty_criteria) {
                const matchingCriteria = warrantyStartCriteriaOptions.find(opt =>
                    opt.title.toLowerCase().includes(cpoRules.warranty_criteria!.toLowerCase().replace("_", " "))
                )
                if (matchingCriteria) {
                    formik.setFieldValue(EAddOnsFields.WARRANTY_START_CRITERIA, matchingCriteria)
                }
            }

            if (cpoRules.amc_required_default !== undefined) {
                formik.setFieldValue(EAddOnsFields.IS_AMC_APPLICABLE, cpoRules.amc_required_default)
            }
        }
    }, [dealTypeOverrides, add_ons_form])

    function hasError(field: EAddOnsFields) {
        return Boolean(formik.submitCount > 0 && formik.errors[field])
    }

    function hasManpowerTypeError(): boolean {
        if (formik.submitCount === 0) return false
        if (!formik.values[EAddOnsFields.IS_MANPOWER_APPLICABLE]) return false

        const hasValidMarshal =
            formik.values[EAddOnsFields.MANPOWER_MARSHAL_ENABLED] &&
            formik.values[EAddOnsFields.MANPOWER_MARSHAL_RESOURCE] &&
            formik.values[EAddOnsFields.MANPOWER_MARSHAL_DURATION]

        const hasValidGuard =
            formik.values[EAddOnsFields.MANPOWER_GUARD_ENABLED] &&
            formik.values[EAddOnsFields.MANPOWER_GUARD_RESOURCE] &&
            formik.values[EAddOnsFields.MANPOWER_GUARD_DURATION]

        const hasValidCustomerSupport =
            formik.values[EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_ENABLED] &&
            formik.values[EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_RESOURCE] &&
            formik.values[EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_DURATION]

        return !(hasValidMarshal || hasValidGuard || hasValidCustomerSupport)
    }

    function handleRadioChange(field: EAddOnsFields, value: DropdownObj) {
        const boolValue = value.id === 1
        formik.setFieldValue(field, boolValue)
    }

    function getRadioValue(field: EAddOnsFields): DropdownObj | undefined {
        const value = formik.values[field]
        if (typeof value === "boolean") {
            return value ? yesNoOptions[0] : yesNoOptions[1]
        }
        return undefined
    }

    function buildManpowerObj(values: AddOnsFormValues): ManpowerObj[] {
        const manpowerObj: ManpowerObj[] = []

        if (values[EAddOnsFields.MANPOWER_MARSHAL_ENABLED]) {
            manpowerObj.push({
                manpower_type: "marshal",
                resource: Number(values[EAddOnsFields.MANPOWER_MARSHAL_RESOURCE]) || 0,
                duration: Number(values[EAddOnsFields.MANPOWER_MARSHAL_DURATION]) || 0,
            })
        }

        if (values[EAddOnsFields.MANPOWER_GUARD_ENABLED]) {
            manpowerObj.push({
                manpower_type: "guard",
                resource: Number(values[EAddOnsFields.MANPOWER_GUARD_RESOURCE]) || 0,
                duration: Number(values[EAddOnsFields.MANPOWER_GUARD_DURATION]) || 0,
            })
        }

        if (values[EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_ENABLED]) {
            manpowerObj.push({
                manpower_type: "customer_support",
                resource: Number(values[EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_RESOURCE]) || 0,
                duration: Number(values[EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_DURATION]) || 0,
            })
        }

        return manpowerObj
    }

    async function handleSubmit(values: AddOnsFormValues) {
        const payload: IAddOnsPageData = {
            page_title: (props.pageData?.title as EProjectFormPages.ADD_ONS) || EProjectFormPages.ADD_ONS,
            project_id: project_id!,
            is_manpower_applicable: values[EAddOnsFields.IS_MANPOWER_APPLICABLE],
            is_o_and_m_required: values[EAddOnsFields.IS_O_AND_M_REQUIRED],
            is_warranty_applicable: values[EAddOnsFields.IS_WARRANTY_APPLICABLE],
            is_amc_applicable: values[EAddOnsFields.IS_AMC_APPLICABLE],
            insurance_required: values[EAddOnsFields.INSURANCE_REQUIRED],
            charger_connectivity_required: values[EAddOnsFields.CHARGER_CONNECTIVITY_REQUIRED],
        }

        if (values[EAddOnsFields.IS_WARRANTY_APPLICABLE]) {
            payload.warranty_duration_in_months = Number(values[EAddOnsFields.WARRANTY_DURATION_IN_MONTHS])
            payload.warranty_start_criteria = values[EAddOnsFields.WARRANTY_START_CRITERIA]?.title
            payload.warranty_start_date = values[EAddOnsFields.WARRANTY_START_DATE]
        }

        if (values[EAddOnsFields.IS_AMC_APPLICABLE]) {
            payload.amc_type = values[EAddOnsFields.AMC_TYPE]?.title
            payload.amc_start_date = values[EAddOnsFields.AMC_START_DATE]
            payload.amc_duration = Number(values[EAddOnsFields.AMC_DURATION])
        }

        if (values[EAddOnsFields.IS_MANPOWER_APPLICABLE]) {
            payload.manpower_obj = buildManpowerObj(values)
        }

        if (values[EAddOnsFields.CHARGER_CONNECTIVITY_REQUIRED]) {
            payload.charger_connection_type = values[EAddOnsFields.CHARGER_CONNECTION_TYPE]?.title?.toLowerCase()
            payload.sim_duration = Number(values[EAddOnsFields.SIM_DURATION])
        }

        try {
            await updateTicketPageData(payload as any).unwrap()

            // Submit pending file uploads
            await submitPendingFiles()

            // Save form values to Redux
            dispatch(setAddOnsForm(values))

            enqueueSnackbar("Add-ons saved successfully", { variant: "success" })
            props.handleNextStep()
        } catch (error) {
            console.error("Error updating add-ons page data:", error)
            enqueueSnackbar("Error saving add-ons", { variant: "error" })
        }
    }

    return {
        states: {
            formik,
            yesNoOptions,
            amcTypeOptions,
            chargerConnectionTypeOptions,
            warrantyStartCriteriaOptions,
            isUpdatingPageData,
            project_id: project_id ?? undefined,
            FILE_TYPES,
            existingAmcDocuments,
            isLoading: isSubmittingFinalUploads || isUpdatingPageData,
        },
        functions: {
            handleSubmit,
            hasError,
            hasManpowerTypeError,
            handleRadioChange,
            getRadioValue,
            handleFileUpload,
        },
    }
}

export default useAddOns
